import { useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { isCapRequired } from "@/api/client";
import { Button } from "@/components/Button";

const site_key = import.meta.env.VITE_RECAPTCHA_SITE_KEY ?? "";

interface CaptchaGateProps {
  /** The refused request's error; the gate renders nothing unless it is requiresCap. */
  error: unknown;
  /** Called when the CAP check passes: re-runs the refused request, unchanged. */
  onRetry: () => void;
}

/**
 * reCAPTCHA v2 gate for when public links get spammed. POST /link and
 * POST /session-create can be refused with 429 requiresCap; when they
 * are, the shopper proves they're human and the refused request is
 * simply retried (no token is sent). If verification errors, show an
 * explanation and let the shopper retry without reloading the cart.
 */
export function CaptchaGate({ error, onRetry }: CaptchaGateProps) {
  const [verificationFailed, setVerificationFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  if (!isCapRequired(error)) return null;

  /** Verified (token non-null): retry the refused request. Expiry fires null and is ignored. */
  function handleVerified(token: string | null): void {
    if (token) onRetry();
  }

  function handleError(): void {
    setVerificationFailed(true);
  }

  return (
    <div className="w-full max-w-md rounded-lg border border-red-200 bg-white p-6 text-center shadow-sm">
      <h1 className="text-lg font-semibold text-gray-900">Please verify you’re human</h1>
      <p className="mt-2 text-sm text-gray-600">
        We’ve received too many requests in a short time, so we need to check that you’re a real person.
      </p>
      {verificationFailed ? (
        <div className="mt-4" role="alert" aria-live="polite">
          <p className="text-sm text-red-700">
            The verification couldn’t load or complete. Wait a moment, then try again.
          </p>
          <Button
            className="mt-4"
            onClick={() => {
              setVerificationFailed(false);
              setAttempt((current) => current + 1);
            }}
          >
            Retry verification
          </Button>
        </div>
      ) : (
        <div className="mt-4 flex justify-center">
          <ReCAPTCHA key={attempt} sitekey={site_key} onChange={handleVerified} onErrored={handleError} />
        </div>
      )}
    </div>
  );
}
